import type { ClientGame, ClientGameCtx } from "./index";
import { HRZ } from "../../shared/arcade";
import { sound } from "../sound";
import { countdownOverlay } from "./asteroids";
import { C, TINT, displayFont, bodyFont, hudPill } from "../theme";
import { cachedGrad, roundRectPath } from "../draw";

interface P { x: number; y: number; vx: number; vy: number; ttl: number; c: string; r: number; g?: number; }
const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);

// Proportional gain for the aim servo, in 1/rad. At full deflection (ax = ±1)
// the server swings the turret at HRZ.AIM_SPEED rad/ms (see
// src/server/games/hormuz.ts tick()).
//
// The servo runs entirely against a LOCAL prediction of the turret angle
// (`predictedAngle`, advanced every render frame in draw()), not the
// networked `st.aimAngle` — that used to be the whole bug. Steering off the
// synced state means "current" is however many milliseconds of round-trip
// stale; the controller keeps commanding correction after the real target
// has already been reached, the true server angle sails past it, and the
// next stale update reverses the correction — classic delayed-feedback
// hunting ("wobble"/overshoot), not a gain problem. Run the identical math
// zero-latency (locally, every frame) and it's provably monotonic: each
// frame, error shrinks by a factor of (1 - HRZ.AIM_SPEED * AIM_GAIN * dt),
// which only oscillates if that factor goes negative, i.e.
// AIM_GAIN > 1 / (HRZ.AIM_SPEED * dt). draw()'s loop caps dt at 50ms, so
// AIM_GAIN = 10 keeps the worst-case factor at 1 - 10*0.0016*50 = 0.2 (still
// positive, still monotonic) while converging to <2% of the initial error in
// ~200ms at a typical 60fps frame time — reads as an immediate snap with no
// overshoot. `predictedAngle` is nudged 5%/frame toward the authoritative
// `st.aimAngle` so a dropped input packet can't leave a permanent gap; at 5%
// that correction is invisible while the servo is actively converging.
const AIM_GAIN = 10;
const SERVER_RECONCILE = 0.05;

// daylight on the strait — the light sticker palette (theme.ts), matching the
// native app's HormuzGame.tsx. friendly = green, enemy = coral/orange — hold
// this line everywhere
const SEA_TOP = "#7CC0FF";
const SEA_BOTTOM = "#4D8DFF";
const SAND = TINT.mango;
const SAND_DARK = "#F5D27A";
const SIGN_GREEN = "#1E8A4E";
const SPENT = "#B9B0DA"; // a knocked-out launch site
const PIP_OFF = "#E6E0FA";
const FRIEND = C.green;
const FRIEND_SOFT = "rgba(55,214,122,0.7)";
const ENEMY = C.coral;
const ENEMY_WARM = "#FF8A3C";
const CONTAINERS = [C.coral, C.cobalt, C.mint, C.mango];
/** CSS px kept clear above the world: the quit button + the hull pill */
const HUD_TOP = 50;
/** CSS px kept clear below the world: FIRE (104px at bottom 28px) + the score stack */
const BOTTOM_UI = 140;
/** max side margin, CSS px, when the world is width-bound */
const SIDE = 6;

/** env(safe-area-inset-*) in CSS px — the page uses viewport-fit=cover */
function readSafeInsets(): { top: number; bottom: number } {
  try {
    const probe = document.createElement("div");
    probe.style.cssText =
      "position:fixed;left:0;top:0;visibility:hidden;pointer-events:none;" +
      "padding-top:env(safe-area-inset-top);padding-bottom:env(safe-area-inset-bottom)";
    document.body.appendChild(probe);
    const cs = getComputedStyle(probe);
    const r = { top: parseFloat(cs.paddingTop) || 0, bottom: parseFloat(cs.paddingBottom) || 0 };
    probe.remove();
    return r;
  } catch {
    return { top: 0, bottom: 0 };
  }
}

/**
 * A white HUD sticker pill holding a row of pips (hull, ammo) — same shape as
 * theme.ts hudPill(): white fill, ink outline, hard ink shadow, 34px tall.
 */
function pipPill(
  g: CanvasRenderingContext2D,
  x: number, y: number,
  label: string, count: number, filled: number, colour: string,
  pipW: number, pipH: number,
  align: "left" | "right" = "right",
) {
  const h = 34, gap = 3;
  g.save();
  g.font = bodyFont(11, 900);
  const lw = g.measureText(label).width;
  const w = Math.round(12 + lw + 8 + count * (pipW + gap) - gap + 12);
  const left = align === "right" ? x - w : x;
  g.fillStyle = C.ink;
  roundRectPath(g, left, y + 3, w, h, h / 2);
  g.fill();
  roundRectPath(g, left, y, w, h, h / 2);
  g.fillStyle = C.white;
  g.fill();
  g.strokeStyle = C.ink;
  g.lineWidth = 2.5;
  g.stroke();
  g.fillStyle = C.muted;
  g.textAlign = "left";
  g.textBaseline = "middle";
  g.fillText(label, left + 12, y + h / 2 + 1);
  let px = left + 12 + lw + 8;
  g.lineWidth = 1.8;
  for (let i = 0; i < count; i++) {
    roundRectPath(g, px, y + (h - pipH) / 2, pipW, pipH, Math.min(3, pipW / 2));
    g.fillStyle = i < filled ? colour : PIP_OFF;
    g.fill();
    g.stroke();
    px += pipW + gap;
  }
  g.restore();
}

export class HormuzClient implements ClientGame {
  private ctx!: ClientGameCtx;
  private canvas!: HTMLCanvasElement;
  private fireBtn!: HTMLDivElement;
  private raf = 0;
  private inputTimer = 0;
  private last = performance.now();
  private parts: P[] = [];
  private t = 0;

  // Tap/drag-to-target aiming — press anywhere above the destroyer to set a
  // target angle; a proportional controller (in the 30ms send loop below)
  // servos the turret toward it every tick. Firing is a separate hold button,
  // same split as the native app. This replaced a floating drag-joystick that
  // mapped raw drag distance to a swing RATE — easy to send the turret
  // swinging past where you wanted with no easy way to slow or reverse it.
  private aiming = false;
  private firing = false;
  private targetAngle: number | null = null;
  private targetPt: { x: number; y: number } | null = null;
  // local, zero-latency estimate of the server's authoritative aimAngle —
  // see the AIM_GAIN comment above for why the servo runs off this instead
  // of the networked value. Advanced every render frame in draw(); the 30ms
  // send loop below just relays whatever ax that last produced.
  private predictedAngle = 0;
  private lastAx = 0;
  // logical<->screen transform, cached from the last draw() so pointer
  // handlers can invert it without recomputing canvas geometry themselves
  private scale = 0;
  private ox = 0;
  private oy = 0;
  private safe = { top: 0, bottom: 0 };

  private get st() { return this.ctx.room.state.hormuz; }

  mount(ctx: ClientGameCtx) {
    this.ctx = ctx;
    sound.unlock();
    this.canvas = document.createElement("canvas");
    this.canvas.id = "game-canvas";
    ctx.root.appendChild(this.canvas);

    this.fireBtn = document.createElement("div");
    this.fireBtn.className = "punch-btn";
    this.fireBtn.textContent = "FIRE";
    const fireOn = (e: Event) => { e.preventDefault(); sound.unlock(); this.fireBtn.classList.add("active"); this.firing = true; };
    const fireOff = (e: Event) => { e.preventDefault(); this.fireBtn.classList.remove("active"); this.firing = false; };
    this.fireBtn.addEventListener("touchstart", fireOn, { passive: false });
    this.fireBtn.addEventListener("touchend", fireOff, { passive: false });
    this.fireBtn.addEventListener("touchcancel", fireOff, { passive: false });
    this.fireBtn.addEventListener("mousedown", fireOn);
    this.fireBtn.addEventListener("mouseup", fireOff);
    this.fireBtn.addEventListener("mouseleave", fireOff);
    ctx.root.appendChild(this.fireBtn);

    const hint = document.createElement("div");
    hint.className = "touch-hint";
    hint.textContent = "tap or drag above your ship to aim · hold FIRE to shoot";
    ctx.root.appendChild(hint);
    setTimeout(() => hint.remove(), 4200);

    this.canvas.addEventListener("touchstart", this.aimStart, { passive: false });
    this.canvas.addEventListener("touchmove", this.aimMove, { passive: false });
    this.canvas.addEventListener("touchend", this.aimEnd);
    this.canvas.addEventListener("touchcancel", this.aimEnd);
    this.canvas.addEventListener("mousedown", this.aimStart as any);
    this.canvas.addEventListener("mousemove", this.aimMove as any);
    this.canvas.addEventListener("mouseup", this.aimEnd as any);
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);

    ctx.room.onMessage("h-fire", () => { sound.unlock(); sound.hzLaunch(); });
    ctx.room.onMessage("h-boom", (m: any) => {
      const k = m?.kills ?? 0;
      if (k > 1) { sound.hzKillBig(); this.burst(m?.x, m?.y, FRIEND, 24, 0.06); }
      else if (k === 1) { sound.hzKill(); this.burst(m?.x, m?.y, FRIEND, 14, 0.05); }
      else { this.burst(m?.x, m?.y, C.white, 5); }
    });
    ctx.room.onMessage("h-hit", (m: any) => { sound.hzShipHit(); this.burst(m?.x, m?.y, ENEMY_WARM, 10); });
    ctx.room.onMessage("h-sink", (m: any) => {
      sound.hzShipLost();
      this.burst(m?.x, m?.y, ENEMY_WARM, 30, 0.1);
      try { navigator.vibrate?.([40, 30, 80]); } catch { /* */ }
    });
    ctx.room.onMessage("h-safe", () => sound.hzEscort());
    ctx.room.onMessage("h-lost", () => { /* covered by h-sink */ });

    this.predictedAngle = this.st.aimAngle ?? 0;
    this.safe = readSafeInsets();

    // draw() (60fps) is what actually advances predictedAngle and computes
    // lastAx every frame — this loop just relays the latest lastAx to the
    // server at a steady 30ms cadence, same as before.
    this.inputTimer = window.setInterval(() => {
      if (this.ctx.room.state.phase !== "playing") return;
      this.ctx.room.send("input", { ax: this.lastAx, ay: 0, fire: this.firing });
    }, 30);
    this.last = performance.now();
    this.loop();
  }

  unmount() {
    cancelAnimationFrame(this.raf);
    clearInterval(this.inputTimer);
    this.canvas.removeEventListener("touchstart", this.aimStart);
    this.canvas.removeEventListener("touchmove", this.aimMove);
    this.canvas.removeEventListener("touchend", this.aimEnd);
    this.canvas.removeEventListener("touchcancel", this.aimEnd);
    this.canvas.removeEventListener("mousedown", this.aimStart as any);
    this.canvas.removeEventListener("mousemove", this.aimMove as any);
    this.canvas.removeEventListener("mouseup", this.aimEnd as any);
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
  }

  // Screen (client) coords -> the target angle a press at that point implies,
  // same geometry as draw()'s render transform (scale/ox/oy, cached from the
  // last frame) and the same atan2(dx, dyUp) the server's turret swings on.
  private updateTarget(clientX: number, clientY: number) {
    if (this.scale <= 0) return;
    const rect = this.canvas.getBoundingClientRect();
    const lx = (clientX - rect.left - this.ox) / this.scale;
    const ly = (clientY - rect.top - this.oy) / this.scale;
    const dx = lx - HRZ.SHIP.x;
    // normal play presses land above the ship (dyUp > 0); floor it so a press
    // at/below the ship's y doesn't send atan2 to the far side of the circle
    // and point the turret backwards
    const dyUp = Math.max(1e-3, HRZ.SHIP.y - ly);
    this.targetAngle = clamp(Math.atan2(dx, dyUp), -HRZ.AIM_MAX, HRZ.AIM_MAX);
    this.targetPt = { x: lx, y: ly };
  }
  private aimStart = (e: any) => {
    e.preventDefault();
    this.aiming = true;
    const t = e.touches?.[0] ?? e;
    this.updateTarget(t.clientX, t.clientY);
  };
  private aimMove = (e: any) => {
    if (!this.aiming) return;
    e.preventDefault();
    const t = e.touches?.[0] ?? e;
    this.updateTarget(t.clientX, t.clientY);
  };
  // deliberately no reset of targetAngle here: lifting the finger leaves the
  // turret servoing toward (and holding at) the last-pressed angle, rather
  // than snapping back to 0 — that's the point of "hold where I aimed"
  private aimEnd = (_e: any) => { this.aiming = false; };

  private onKeyDown = (e: KeyboardEvent) => {
    if (e.code === "ArrowLeft" || e.code === "ArrowRight") {
      const cur = this.targetAngle ?? this.predictedAngle;
      this.targetAngle = clamp(cur + (e.code === "ArrowLeft" ? -0.12 : 0.12), -HRZ.AIM_MAX, HRZ.AIM_MAX);
      this.targetPt = null; // no on-canvas press point to show a crosshair at
      e.preventDefault();
    } else if (e.code === "Space") {
      this.firing = true;
      e.preventDefault();
    }
  };
  private onKeyUp = (e: KeyboardEvent) => {
    if (e.code === "Space") this.firing = false;
  };

  private burst(x = 0, y = 0, c: string = C.white, n = 10, spread = 0.09) {
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const v = 0.02 + Math.random() * spread;
      this.parts.push({
        x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v,
        ttl: 320 + Math.random() * 420, c, r: 1 + Math.random() * 2.4,
      });
    }
  }

  private smoke(x: number, y: number) {
    this.parts.push({
      x: x + (Math.random() - 0.5) * 6, y, vx: (Math.random() - 0.5) * 0.01, vy: -0.02 - Math.random() * 0.02,
      ttl: 700 + Math.random() * 500, c: C.muted, r: 3 + Math.random() * 3, g: 1,
    });
  }

  private loop = () => {
    const now = performance.now();
    const dt = Math.min(50, now - this.last);
    this.last = now;
    this.t += dt;
    try { this.draw(dt); } catch (e) { console.error("hormuz frame", e); }
    this.raf = requestAnimationFrame(this.loop);
  };

  private draw(dt: number) {
    const { room } = this.ctx;
    const st = this.st;

    // advance the local aim prediction this frame (see the AIM_GAIN comment
    // up top) — this is the servo; the 30ms interval only relays its output
    if (this.targetAngle !== null) {
      // plain subtraction, no wraparound handling: both target and predicted
      // are clamped to ±HRZ.AIM_MAX (~1.4 rad), nowhere near the ±π boundary
      // where angle wraparound would actually matter
      const error = this.targetAngle - this.predictedAngle;
      this.lastAx = clamp(error * AIM_GAIN, -1, 1);
      this.predictedAngle = clamp(this.predictedAngle + this.lastAx * HRZ.AIM_SPEED * dt, -HRZ.AIM_MAX, HRZ.AIM_MAX);
    } else {
      this.lastAx = 0;
    }
    // gently reconcile toward the authoritative server angle so a dropped
    // input packet can't leave a permanent gap between the two
    this.predictedAngle += ((st.aimAngle ?? 0) - this.predictedAngle) * SERVER_RECONCILE;

    const c = this.canvas;
    const g = c.getContext("2d")!;
    const dpr = Math.min(2, devicePixelRatio || 1);
    const cw = c.clientWidth, ch = c.clientHeight;
    if (cw < 40 || ch < 40) return;
    if (c.width !== cw * dpr || c.height !== ch * dpr) {
      c.width = cw * dpr; c.height = ch * dpr;
      this.safe = readSafeInsets();
    }
    g.save();
    g.scale(dpr, dpr);
    g.lineJoin = "round";
    g.lineCap = "round";

    // The world fits between the top HUD row and the bottom controls (FIRE +
    // the score stack), as wide as the screen allows; the sea and both coasts
    // then carry on past its edges so the scene fills the whole canvas.
    const top = this.safe.top + HUD_TOP;
    const bottom = this.safe.bottom + BOTTOM_UI;
    const fitH = Math.max(40, ch - top - bottom);
    const scale = Math.min((cw - SIDE * 2) / HRZ.W, fitH / HRZ.H);
    const ox = (cw - HRZ.W * scale) / 2;
    const oy = top + Math.max(0, (fitH - HRZ.H * scale) / 2);
    // cache for the pointer handlers, which need to invert this same transform
    this.scale = scale; this.ox = ox; this.oy = oy;
    // world units past each edge of the world, out to the canvas edges
    const padL = ox / scale + 2;
    const padT = oy / scale + 2;
    const padB = (ch - oy) / scale - HRZ.H + 2;
    const fullW = HRZ.W + padL * 2;

    g.save();
    g.translate(ox, oy);
    g.scale(scale, scale);
    const px1 = 1 / scale; // one CSS px, in world units

    // ---- the sea, running past the world's edges ----
    g.fillStyle = cachedGrad("hrz-sea", () => {
      const gr = g.createLinearGradient(0, HRZ.COAST_N, 0, HRZ.COAST_S);
      gr.addColorStop(0, SEA_TOP);
      gr.addColorStop(1, SEA_BOTTOM);
      return gr;
    });
    g.fillRect(-padL, HRZ.COAST_N, fullW, HRZ.COAST_S - HRZ.COAST_N);
    // drifting wave crests
    g.save();
    g.strokeStyle = "rgba(255,255,255,0.38)";
    g.lineWidth = 2;
    g.setLineDash([30, 90]);
    for (let i = 0; i < 9; i++) {
      const y = HRZ.COAST_N + 16 + i * 46 + Math.sin(this.t / 900 + i) * 3;
      const x0 = ((this.t / 40 + i * 53) % 120) - 120 - padL;
      g.beginPath();
      for (let x = x0; x < HRZ.W + padL; x += 30) {
        g.moveTo(x, y);
        g.quadraticCurveTo(x + 15, y - 4, x + 30, y);
      }
      g.stroke();
    }
    g.restore();
    // coasts: sand to the top and bottom of the screen, a darker wet-sand
    // band at the waterline and an ink shore line
    g.fillStyle = SAND;
    g.fillRect(-padL, -padT, fullW, HRZ.COAST_N + padT);
    g.fillRect(-padL, HRZ.COAST_S, fullW, HRZ.H - HRZ.COAST_S + padB);
    g.fillStyle = SAND_DARK;
    g.fillRect(-padL, HRZ.COAST_N - 8, fullW, 8);
    g.fillRect(-padL, HRZ.COAST_S, fullW, 6);
    g.strokeStyle = C.ink;
    g.lineWidth = 2.5;
    g.beginPath();
    g.moveTo(-padL, HRZ.COAST_N); g.lineTo(HRZ.W + padL, HRZ.COAST_N);
    g.moveTo(-padL, HRZ.COAST_S); g.lineTo(HRZ.W + padL, HRZ.COAST_S);
    g.stroke();

    // ---- "BADDIES" road sign — names the far shore the threats launch from ----
    g.save();
    g.translate(HRZ.W / 2, 22);
    // posts
    g.fillStyle = C.ink;
    g.fillRect(-52, 10, 4, 20);
    g.fillRect(48, 10, 4, 20);
    // hard ink shadow, green board, ink outline
    g.fillRect(-65, -12, 134, 28);
    g.fillStyle = SIGN_GREEN;
    g.fillRect(-68, -15, 134, 28);
    g.strokeStyle = C.ink;
    g.lineWidth = 2;
    g.strokeRect(-68, -15, 134, 28);
    g.fillStyle = C.white;
    g.font = displayFont(15);
    g.textAlign = "center";
    g.textBaseline = "middle";
    g.fillText("BADDIES", -1, 0.5);
    g.restore();

    // ---- land sites (ENEMY) ----
    st.sites?.forEach((s: any) => {
      g.save();
      g.translate(s.x, s.y);
      const pulse = 0.5 + 0.5 * Math.sin(this.t / 110);
      if (s.state === 1) {
        // telegraph: bright charging dome
        g.fillStyle = `rgba(255,107,91,${0.3 + 0.4 * pulse})`;
        g.beginPath(); g.arc(0, 0, 8 + 7 * pulse, 0, Math.PI * 2); g.fill();
      }
      // bunker
      g.beginPath();
      g.moveTo(-9, 5); g.lineTo(-6, -5); g.lineTo(6, -5); g.lineTo(9, 5);
      g.closePath();
      g.fillStyle = s.state === 2 ? SPENT : ENEMY;
      g.fill();
      g.strokeStyle = C.ink;
      g.lineWidth = 1.6;
      g.stroke();
      g.fillStyle = C.ink;
      g.fillRect(-9, 4, 18, 3);
      // barrel / rail pointing into the strait
      const bd = s.side === 0 ? 1 : -1;
      g.lineWidth = s.kind === 1 ? 3 : 2;
      g.beginPath(); g.moveTo(0, -2); g.lineTo(0, bd * (s.kind === 1 ? 12 : 8) - 2); g.stroke();
      if (s.state !== 2) {
        g.fillStyle = C.white;
        g.beginPath(); g.arc(0, -2, 2, 0, Math.PI * 2); g.fill();
      }
      g.restore();
    });

    // ---- mines (ENEMY) ----
    st.mines?.forEach((mn: any) => {
      g.save();
      g.translate(mn.x, mn.y);
      if (mn.arm > 0) {
        const p = 1 - mn.arm / HRZ.MINE_ARM_MS;
        g.strokeStyle = `rgba(255,107,91,${1 - p})`;
        g.lineWidth = 2.5;
        g.beginPath(); g.arc(0, 0, 3 + p * 13, 0, Math.PI * 2); g.stroke();
      } else {
        const glow = 0.35 + 0.3 * Math.sin(this.t / 200);
        g.fillStyle = `rgba(255,107,91,${glow})`;
        g.beginPath(); g.arc(0, 0, 9, 0, Math.PI * 2); g.fill();
      }
      // spiked body
      g.strokeStyle = C.ink;
      g.lineWidth = 1.8;
      g.beginPath();
      for (let k = 0; k < 8; k++) {
        const a = (k / 8) * Math.PI * 2;
        g.moveTo(Math.cos(a) * 3.5, Math.sin(a) * 3.5); g.lineTo(Math.cos(a) * 7, Math.sin(a) * 7);
      }
      g.stroke();
      g.fillStyle = C.ink;
      g.beginPath(); g.arc(0, 0, 4.8, 0, Math.PI * 2); g.fill();
      g.fillStyle = ENEMY;
      g.beginPath(); g.arc(0, 0, mn.arm <= 0 && Math.floor(this.t / 300) % 2 === 0 ? 2 : 1.2, 0, Math.PI * 2); g.fill();
      g.restore();
    });

    // ---- protected ships (FRIENDLY) ----
    st.ships?.forEach((s: any) => {
      const len = s.kind === 0 ? 40 : 30;
      const wid = s.kind === 0 ? 15 : 11;
      const face = s.vx >= 0 ? 1 : -1;
      const sinking = s.state === 1;
      const list = sinking ? (1 - s.sink / 2200) * 0.6 : 0;
      // shadow on the water
      g.save();
      g.translate(s.x, s.y + 4);
      g.scale(1, 0.4);
      g.fillStyle = "rgba(27,21,52,0.22)";
      g.beginPath(); g.ellipse(0, 0, len / 2 + 2, wid / 2 + 2, 0, 0, Math.PI * 2); g.fill();
      g.restore();

      g.save();
      g.translate(s.x, s.y);
      g.scale(face, 1);
      g.rotate(list);
      if (sinking) g.globalAlpha = Math.max(0.2, s.sink / 2200);

      // bow wave + wake
      if (!sinking) {
        g.fillStyle = "rgba(255,255,255,0.7)";
        g.beginPath(); g.ellipse(len / 2 - 1, 0, 4, wid / 2, 0, 0, Math.PI * 2); g.fill();
        g.fillStyle = "rgba(255,255,255,0.3)";
        g.beginPath();
        g.moveTo(-len / 2, -wid / 3); g.lineTo(-len / 2 - 22, -wid * 0.8);
        g.lineTo(-len / 2 - 22, wid * 0.8); g.lineTo(-len / 2, wid / 3);
        g.closePath(); g.fill();
      }

      // hull — an ink hull with a bright deck inset, so the ink reads as the outline
      g.fillStyle = C.ink;
      g.beginPath();
      g.moveTo(len / 2, 0); g.lineTo(len / 2 - 7, wid / 2);
      g.lineTo(-len / 2 + 2, wid / 2); g.lineTo(-len / 2, wid / 2 - 2);
      g.lineTo(-len / 2, -wid / 2 + 2); g.lineTo(-len / 2 + 2, -wid / 2);
      g.lineTo(len / 2 - 7, -wid / 2);
      g.closePath(); g.fill();
      // deck (tanker mango, cargo white)
      g.fillStyle = s.kind === 0 ? C.mango : C.white;
      g.beginPath();
      g.moveTo(len / 2 - 2, -1.5); g.lineTo(len / 2 - 8, -wid / 2 + 1);
      g.lineTo(-len / 2 + 2, -wid / 2 + 1); g.lineTo(-len / 2 + 2, wid / 2 - 3);
      g.lineTo(len / 2 - 8, wid / 2 - 3);
      g.closePath(); g.fill();

      if (s.kind === 0) {
        // tanker: manifold domes
        g.fillStyle = C.white;
        for (let k = -1; k <= 1; k++) {
          g.beginPath(); g.arc(k * 8, -1, 3.2, 0, Math.PI * 2); g.fill();
        }
      } else {
        // cargo: container stacks
        for (let k = 0; k < 4; k++) {
          g.fillStyle = CONTAINERS[k]!;
          g.fillRect(-len / 2 + 6 + k * 5, -wid / 2 + 2, 4, 4);
        }
      }
      // bridge/superstructure at the stern — low + wide, with an ink cast edge
      g.fillStyle = C.ink;
      g.fillRect(-len / 2 + 2.5, -wid / 2 - 3.5, 8, wid + 1);
      g.fillStyle = C.white;
      g.fillRect(-len / 2 + 3, -wid / 2 - 4, 7, wid);
      // green friendly nav light
      g.fillStyle = FRIEND;
      g.beginPath(); g.arc(len / 2 - 3, 0, 1.5, 0, Math.PI * 2); g.fill();
      g.restore();

      if (!sinking) {
        // friendly bracket above — reads as "protect me"
        g.strokeStyle = FRIEND_SOFT;
        g.lineWidth = 1.4;
        const by = s.y - wid / 2 - 9;
        g.beginPath();
        g.moveTo(s.x - 7, by + 3); g.lineTo(s.x - 7, by); g.lineTo(s.x - 3, by);
        g.moveTo(s.x + 3, by); g.lineTo(s.x + 7, by); g.lineTo(s.x + 7, by + 3);
        g.stroke();
        // hp pips
        if (s.hp < s.maxhp) {
          for (let k = 0; k < s.maxhp; k++) {
            g.fillStyle = k < s.hp ? FRIEND : ENEMY;
            g.fillRect(s.x - s.maxhp * 2 + k * 4, by - 4, 3, 2);
          }
        }
      } else {
        this.smoke(s.x, s.y - wid);
        if (Math.random() < 0.4) this.burst(s.x + (Math.random() - 0.5) * len, s.y, ENEMY_WARM, 1);
      }
    });

    // ---- incoming projectiles (ENEMY) ----
    st.projs?.forEach((p: any) => {
      const ang = Math.atan2(p.vy, p.vx);
      // targeting laser to the nearest live ship (drone) — makes intent obvious
      if (p.kind === 0) {
        let bx = 0, by = 0, bd = 1e9;
        st.ships?.forEach((s: any) => {
          if (s.state !== 0) return;
          const d = (s.x - p.x) ** 2 + (s.y - p.y) ** 2;
          if (d < bd) { bd = d; bx = s.x; by = s.y; }
        });
        if (bd < 1e9) {
          g.strokeStyle = `rgba(255,107,91,${0.35 + 0.25 * Math.sin(this.t / 90)})`;
          g.lineWidth = 1.2;
          g.beginPath(); g.moveTo(p.x, p.y); g.lineTo(bx, by); g.stroke();
        }
      }
      g.save();
      g.translate(p.x, p.y);
      g.rotate(ang);
      if (p.kind === 1) {
        // ballistic missile: white smoke trail + coral warhead
        g.strokeStyle = "rgba(255,255,255,0.8)";
        g.lineWidth = 4 + Math.sin(this.t / 40) * 1;
        g.beginPath(); g.moveTo(-22, 0); g.lineTo(2, 0); g.stroke();
        g.beginPath(); g.moveTo(7, 0); g.lineTo(-5, -3.5); g.lineTo(-5, 3.5); g.closePath();
        g.fillStyle = ENEMY; g.fill();
        g.strokeStyle = C.ink; g.lineWidth = 1.2; g.stroke();
      } else {
        // drone: ink X frame, coral rotors + body
        g.strokeStyle = C.ink;
        g.lineWidth = 2.5;
        g.beginPath(); g.moveTo(-4.5, -4.5); g.lineTo(4.5, 4.5); g.moveTo(-4.5, 4.5); g.lineTo(4.5, -4.5); g.stroke();
        g.fillStyle = ENEMY;
        g.beginPath(); g.arc(-4.5, -4.5, 1.8, 0, 6.28); g.arc(4.5, 4.5, 1.8, 0, 6.28); g.fill();
        g.beginPath(); g.arc(-4.5, 4.5, 1.8, 0, 6.28); g.arc(4.5, -4.5, 1.8, 0, 6.28); g.fill();
        g.fillRect(-3, -3, 6, 6);
        g.lineWidth = 1.2;
        g.strokeRect(-3, -3, 6, 6);
      }
      g.restore();
    });

    // ---- interceptors (FRIENDLY) ----
    st.shots?.forEach((s: any) => {
      g.strokeStyle = "rgba(255,255,255,0.6)";
      g.lineWidth = 2;
      g.beginPath(); g.moveTo(HRZ.SHIP.x, HRZ.SHIP.y); g.lineTo(s.x, s.y); g.stroke();
      g.beginPath(); g.arc(s.x, s.y, 3.4, 0, Math.PI * 2);
      g.fillStyle = FRIEND; g.fill();
      g.strokeStyle = C.ink; g.lineWidth = 1.2; g.stroke();
    });

    // ---- blasts (green = your kill) ----
    st.blasts?.forEach((b: any) => {
      const p = 1 - b.life / 260;
      const r = Math.max(0.1, b.r * p);
      g.fillStyle = `rgba(255,255,255,${0.9 * (1 - p)})`;
      g.beginPath(); g.arc(b.x, b.y, r, 0, Math.PI * 2); g.fill();
      g.strokeStyle = `rgba(55,214,122,${0.9 * (1 - p)})`;
      g.lineWidth = 3;
      g.stroke();
    });

    // ---- particles ----
    for (let i = this.parts.length - 1; i >= 0; i--) {
      const p = this.parts[i]!;
      p.ttl -= dt; if (p.ttl <= 0) { this.parts.splice(i, 1); continue; }
      p.x += p.vx * dt; p.y += p.vy * dt;
      if (!p.g) p.vy += 0.00005 * dt;
      g.globalAlpha = Math.min(0.9, p.ttl / 400) * (p.g ? 0.5 : 1);
      g.fillStyle = p.c;
      if (p.g) { g.beginPath(); g.arc(p.x, p.y, p.r, 0, 6.28); g.fill(); }
      else g.fillRect(p.x - p.r / 2, p.y - p.r / 2, p.r, p.r);
      g.globalAlpha = 1;
    }

    // ---- your warship (FRIENDLY) ----
    g.save();
    g.translate(HRZ.SHIP.x, HRZ.SHIP.y);
    const sweep = (this.t / 1300) % (Math.PI * 2);
    g.fillStyle = cachedGrad("hrz-radar", () => {
      const rg = g.createRadialGradient(0, 0, 0, 0, 0, 66);
      rg.addColorStop(0, "rgba(255,255,255,0.4)");
      rg.addColorStop(1, "rgba(255,255,255,0)");
      return rg;
    });
    g.beginPath(); g.moveTo(0, 0); g.arc(0, 0, 66, sweep - 0.45, sweep); g.closePath(); g.fill();
    // shadow
    g.fillStyle = "rgba(27,21,52,0.22)";
    g.beginPath(); g.ellipse(0, 5, 24, 8, 0, 0, Math.PI * 2); g.fill();
    // hull: white sticker with an ink outline and a green deck stripe
    g.beginPath();
    g.moveTo(-24, 0); g.lineTo(-18, -8); g.lineTo(16, -8); g.lineTo(24, 0); g.lineTo(16, 7); g.lineTo(-18, 7);
    g.closePath();
    g.fillStyle = C.white; g.fill();
    g.strokeStyle = C.ink; g.lineWidth = 2; g.stroke();
    g.fillStyle = FRIEND;
    g.fillRect(-18, -8, 34, 3);
    // superstructure + mast
    g.fillStyle = C.white;
    g.fillRect(-8, -15, 14, 9);
    g.lineWidth = 1.6;
    g.strokeRect(-8, -15, 14, 9);
    g.fillStyle = C.ink;
    g.fillRect(-4, -13, 6, 3);
    g.beginPath(); g.moveTo(0, -15); g.lineTo(0, -23); g.stroke();
    // gun turret — swings left/right only, dead ahead is straight up. Uses
    // the local prediction (not st.aimAngle) so the barrel, the dashed aim
    // guide below, and where a fired shot actually launches all agree —
    // predictedAngle tracks the server's own physics closely enough (same
    // math, same ax) that this never visibly diverges from what the server
    // will do when FIRE is pressed.
    const ang = this.predictedAngle;
    g.lineWidth = 3;
    g.beginPath(); g.moveTo(0, 0); g.lineTo(Math.sin(ang) * 13, -Math.cos(ang) * 13); g.stroke();
    g.beginPath(); g.arc(0, -1, 4.5, 0, Math.PI * 2); g.fill();
    // green running lights
    g.fillStyle = FRIEND;
    g.beginPath(); g.arc(-18, 0, 1.4, 0, 6.28); g.arc(16, -3, 1.4, 0, 6.28); g.fill();
    g.restore();

    // ---- aim guide — where the next shot will fly, left/right to steer it ----
    const rdy = st.ammo > 0;
    g.save();
    g.strokeStyle = rdy ? "rgba(255,255,255,0.85)" : "rgba(255,107,91,0.85)";
    g.lineWidth = 2;
    g.lineCap = "butt";
    g.setLineDash([5, 7]);
    g.beginPath();
    g.moveTo(HRZ.SHIP.x, HRZ.SHIP.y);
    g.lineTo(HRZ.SHIP.x + Math.sin(ang) * 700, HRZ.SHIP.y - Math.cos(ang) * 700);
    g.stroke();
    g.restore();

    // ---- target crosshair — where the player is currently pressing (or last
    // pressed), so it's visually obvious how the aim line connects to it.
    // White over a thin ink under-stroke so it reads on sea and sand alike ----
    if (this.targetPt) {
      g.save();
      g.translate(this.targetPt.x, this.targetPt.y);
      const cross = () => {
        g.beginPath(); g.arc(0, 0, 13, 0, Math.PI * 2);
        g.moveTo(-20, 0); g.lineTo(-7, 0); g.moveTo(7, 0); g.lineTo(20, 0);
        g.moveTo(0, -20); g.lineTo(0, -7); g.moveTo(0, 7); g.lineTo(0, 20);
      };
      cross();
      g.strokeStyle = "rgba(27,21,52,0.55)";
      g.lineWidth = 2.4 + 2 * px1;
      g.stroke();
      cross();
      g.strokeStyle = C.white;
      g.lineWidth = 2.4;
      g.stroke();
      g.fillStyle = C.white;
      g.beginPath(); g.arc(0, 0, 2.4, 0, Math.PI * 2); g.fill();
      g.restore();
    }
    g.restore();

    // ---- HUD (screen space, clear of the quit button top-left and the
    // best-score banner top-centre) ----
    const hudY = this.safe.top + 8;
    // hull = ships you can still lose (green pips), top-right
    pipPill(g, cw - 12, hudY, "Hull", HRZ.HULL, st.hull ?? 0, FRIEND, 10, 15);
    // bottom-left stack, level with the FIRE button: score, clock, ammo
    const baseY = ch - this.safe.bottom - 28;
    const secs = Math.floor((st.elapsed ?? 0) / 1000);
    const pipH = 34;
    pipPill(g, 12, baseY - pipH, "Ammo", HRZ.AMMO_MAX, st.ammo ?? 0, C.mango, 7, 15, "left");
    hudPill(g, 12, baseY - pipH - 34, `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")}`, {
      size: 13, label: `${st.escorted ?? 0} escorted ·`,
    });
    hudPill(g, 12, baseY - pipH - 34 - 44, Math.floor(st.score ?? 0).toLocaleString(), { size: 18, label: "Score" });

    if (room.state.phase === "countdown") countdownOverlay(g, cw, ch, room.state.countdown);
    g.restore();
  }

  results(ctx: ClientGameCtx): HTMLElement {
    const st = ctx.room.state.hormuz;
    const d = document.createElement("div");
    d.className = "arc-final";
    const secs = Math.floor((st.elapsed ?? 0) / 1000);
    d.innerHTML =
      `<div class="score-big">${Math.floor(st.score ?? 0).toLocaleString()}</div>` +
      `<div class="arc-note">held the strait ${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")} · ${st.escorted ?? 0} ships escorted</div>`;
    return d;
  }
}
